import { type LeadContact } from '@/models/salesLead'
import { normalizeE164 } from '@/utils/phone'
import { type AircallNumber, NumberLabel } from './types'

/**
 * Reachable phone numbers for a lead contact, as Aircall dropdown entries:
 * the primary phone (label Phone) followed by each secondary phone (label Cell).
 * Numbers are normalized to E.164, deduped, and blanks dropped.
 */
export function contactToNumbers(contact: LeadContact | null | undefined): AircallNumber[] {
  if (!contact) return []
  const numbers: AircallNumber[] = []
  const primary = normalizeE164(contact.primaryPhone)
  if (primary) numbers.push({ label: NumberLabel.Phone, value: primary })
  // Guard against partial payloads (deploy skew) where secondaryPhones is absent
  // despite the type — iterating undefined here would crash the whole Activity tab.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  for (const raw of contact.secondaryPhones ?? []) {
    const e164 = normalizeE164(raw)
    if (e164 && !numbers.some((n) => n.value === e164)) {
      numbers.push({ label: NumberLabel.Cell, value: e164 })
    }
  }
  return numbers
}

/**
 * All reachable numbers across a lead's contacts (primary contact first, as the
 * API orders them), deduped by value. With a single contact the Phone/Cell
 * labels are kept; with multiple contacts each number is labeled by its
 * contact's name (falling back to Phone/Cell) so the dropdown shows who.
 */
export function leadContactNumbers(contacts: LeadContact[]): AircallNumber[] {
  const multi = contacts.length > 1
  const numbers: AircallNumber[] = []
  const seen = new Set<string>()
  for (const contact of contacts) {
    for (const n of contactToNumbers(contact)) {
      if (seen.has(n.value)) continue
      seen.add(n.value)
      numbers.push(multi ? { label: contact.name || n.label, value: n.value } : n)
    }
  }
  return numbers
}
